'use strict';

Object.defineProperty(exports, '__esModule', { value: true });

var _slicedToArray = require('@babel/runtime/helpers/slicedToArray');
var react = require('react');
var PropTypes = require('prop-types');
var core = require('@popperjs/core');
var PopupManagerContext = require('../../components/alloy-popup/PopupManagerContext.js');
var utils = require('./utils.js');
var useClickawayListener = require('../useClickawayListener/useClickawayListener.js');
var useEscapeKeyListener = require('../useEscapeKeyListener/useEscapeKeyListener.js');

function _interopDefault (e) { return e && e.__esModule ? e : { default: e }; }

var _slicedToArray__default = /*#__PURE__*/_interopDefault(_slicedToArray);
var PropTypes__default = /*#__PURE__*/_interopDefault(PropTypes);

var DEFAULT_POPPER_OPTIONS = {
  placement: 'bottom'
};
var usePopup = function usePopup(props) {
  var _ref = props || {},
    _ref$excludeFromPopup = _ref.excludeFromPopupManager,
    excludeFromPopupManager = _ref$excludeFromPopup === void 0 ? false : _ref$excludeFromPopup,
    _ref$hideArrow = _ref.hideArrow,
    hideArrow = _ref$hideArrow === void 0 ? false : _ref$hideArrow,
    _ref$showCloseButton = _ref.showCloseButton,
    showCloseButton = _ref$showCloseButton === void 0 ? false : _ref$showCloseButton,
    id = _ref.id,
    onClose = _ref.onClose,
    _ref$popperOptions = _ref.popperOptions,
    popperOptions = _ref$popperOptions === void 0 ? DEFAULT_POPPER_OPTIONS : _ref$popperOptions,
    role = _ref.role,
    _ref$showInitially = _ref.showInitially,
    showInitially = _ref$showInitially === void 0 ? false : _ref$showInitially,
    _ref$sticky = _ref.sticky,
    sticky = _ref$sticky === void 0 ? false : _ref$sticky;
  var _useState = react.useState(false),
    _useState2 = _slicedToArray__default.default(_useState, 2),
    display = _useState2[0],
    setDisplay = _useState2[1];
  var popupContainerRef = react.useRef(null);
  var initiatorRef = react.useRef(null);
  var popperRef = react.useRef(null);
  var _useContext = react.useContext(PopupManagerContext.default),
    closeCurrent = _useContext.closeCurrent,
    register = _useContext.register,
    unregister = _useContext.unregister;
  var normalizedId = id ? id : utils.getRandomId();
  var popupId = "".concat(normalizedId, "_content");

  // we add the timeout to push this code to the end of the event queue to give the initiator time to render so the arrow displays correctly
  // https://stackoverflow.com/questions/15875128/how-to-tell-when-a-dynamically-created-element-has-rendered
  var recalculatePosition = function recalculatePosition() {
    return setTimeout(function () {
      var _popperRef$current;
      return popperRef === null || popperRef === void 0 || (_popperRef$current = popperRef.current) === null || _popperRef$current === void 0 ? void 0 : _popperRef$current.update();
    }, 0);
  };
  var closePopup = react.useCallback(function () {
    var shouldClose = onClose ? onClose(initiatorRef, popupContainerRef) : true;
    if (shouldClose) {
      unregister();
    }
    setDisplay(!shouldClose);
  }, [onClose, unregister]);
  var handleRegistration = react.useCallback(function () {
    if (!sticky && !excludeFromPopupManager) {
      register(closePopup);
    }
  }, [closePopup, excludeFromPopupManager, register, sticky]);
  var openPopup = react.useCallback(function () {
    var _popperRef$current2;
    closeCurrent();
    setDisplay(true);
    handleRegistration();
    popperRef === null || popperRef === void 0 || (_popperRef$current2 = popperRef.current) === null || _popperRef$current2 === void 0 || _popperRef$current2.update();
  }, [closeCurrent, handleRegistration]);
  var togglePopup = react.useCallback(function () {
    var newVisibleState = !display;
    if (newVisibleState) {
      openPopup();
    } else {
      closePopup();
    }
  }, [closePopup, display, openPopup]);
  var handleClickaway = react.useCallback(function () {
    if (!sticky) {
      closePopup();
    }
  }, [closePopup, sticky]);
  useClickawayListener.useClickawayListener([popupContainerRef, initiatorRef], handleClickaway);
  useEscapeKeyListener.useEscapeKeyListener(closePopup);
  react.useEffect(function () {
    popperRef.current = core.createPopper(initiatorRef.current, popupContainerRef.current, popperOptions);
    if (popperRef.current && showInitially) {
      // we add the timeout to push this code to the end of the event queue to give the initiator time to render so the arrow displays correctly
      // https://stackoverflow.com/questions/15875128/how-to-tell-when-a-dynamically-created-element-has-rendered
      setTimeout(function () {
        var _popperRef$current3;
        setDisplay(true);
        handleRegistration();
        popperRef === null || popperRef === void 0 || (_popperRef$current3 = popperRef.current) === null || _popperRef$current3 === void 0 || _popperRef$current3.update();
      }, 0);
    }
    return function () {
      popperRef.current.destroy(); // clean up
    };
  }, [closePopup, excludeFromPopupManager, handleRegistration, popperOptions, register, showInitially, sticky]);
  var popupProps = {
    'aria-labelledBy': normalizedId,
    labelledBy: normalizedId,
    showCloseButton: showCloseButton,
    hideArrow: hideArrow,
    id: popupId,
    ref: popupContainerRef,
    role: role,
    sticky: sticky
  };
  var initiatorProps = {
    'aria-haspopup': 'true',
    'aria-controls': popupId,
    'aria-expanded': display ? 'true' : 'false',
    id: normalizedId,
    ref: initiatorRef
  };
  return {
    closePopup: closePopup,
    isDisplayed: display,
    initiatorProps: initiatorProps,
    openPopup: openPopup,
    popupProps: popupProps,
    recalculatePosition: recalculatePosition,
    togglePopup: togglePopup
  };
};
usePopup.propTypes = {
  /** Skips registration of the popup's close callback with PopupManager if true */
  excludeFromPopupManager: PropTypes__default.default.bool,
  /** Hides the Popup's arrow */
  hideArrow: PropTypes__default.default.bool,
  /** Shows the Popup's close button */
  showCloseButton: PropTypes__default.default.bool,
  /** An id to be applied to the initiator. An id in the format ${id}_content will also be applied to the PopupContainer */
  id: PropTypes__default.default.string,
  /** A callback function that can be used to notify the parent when the popup closes */
  onClose: PropTypes__default.default.func,
  /** A callback function that can be used to notify the parent when the popup shows */
  onOpen: PropTypes__default.default.func,
  /** Modifiers which can be applied to popper to alter the default behavior. More information available at https://popper.js.org/docs/v2/modifiers/ */
  popperOptions: PropTypes__default.default.shape({
    placement: PropTypes__default.default.string,
    modifiers: PropTypes__default.default.arrayOf(PropTypes__default.default.object)
  }),
  /** A role to be applied to the Popup */
  role: PropTypes__default.default.string,
  /** True if Popup should be shown initially. False if it should be hidden initially */
  showInitially: PropTypes__default.default.bool,
  /** True if the Popup only should toggle when clicking on the initiator. False if it should disappear on any clickaway */
  sticky: PropTypes__default.default.bool
};

exports.usePopup = usePopup;
//# sourceMappingURL=usePopup.js.map
